import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Store } from "lucide-react";
import { partnerApply } from "../lib/api";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";

const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };

export default function Partenariat() {
  const [form, setForm] = useState({
    restaurant_name: "", contact_name: "", email: "", phone: "",
    address: "", base: "payerne", catalog_type: "restaurant", specialty: "", desired_tier: "premium", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.restaurant_name.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error("Nom du restaurant, contact et email sont requis.");
      return;
    }
    setBusy(true);
    try {
      await partnerApply(form);
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi de la demande.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="partenariat-success">
        <CheckCircle2 size={56} color={GREEN} className="mx-auto mb-4" />
        <h1 className="text-2xl font-extrabold" style={{ color: GREEN }}>Demande envoyée&nbsp;!</h1>
        <p className="mt-3" style={{ color: "#64748b" }}>
          Merci. Votre demande de partenariat a bien été transmise à La BASE. Nous étudions chaque
          dossier et vous recontactons pour l&apos;activation de votre accès (Gold ou Premium).
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="partenariat-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: GREEN }}>
          <Store size={22} color="#fff" />
        </div>
        <h1 className="text-3xl font-extrabold" style={{ color: GREEN }}>Devenir partenaire</h1>
      </div>
      <p className="text-sm mb-7" style={{ color: "#64748b" }}>
        Remplissez votre fiche de renseignements. La BASE validera votre dossier et vous ouvrira
        l&apos;accès à votre espace partenaire selon votre abonnement.
      </p>

      <div className="grid gap-3">
        <Input testid="pa-name" label="Nom du restaurant *" value={form.restaurant_name} onChange={(v) => set("restaurant_name", v)} />
        <div className="grid grid-cols-2 gap-3">
          <Input testid="pa-contact" label="Personne de contact *" value={form.contact_name} onChange={(v) => set("contact_name", v)} />
          <Input testid="pa-email" label="Email *" value={form.email} onChange={(v) => set("email", v)} type="email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input testid="pa-phone" label="Téléphone" value={form.phone} onChange={(v) => set("phone", v)} />
          <Input testid="pa-specialty" label="Spécialité (ex : Pizzeria, Thaï)" value={form.specialty} onChange={(v) => set("specialty", v)} />
        </div>
        <Input testid="pa-address" label="Adresse" value={form.address} onChange={(v) => set("address", v)} />

        <div>
          <label className="text-xs font-bold" style={{ color: "#64748b" }}>Catalogue *</label>
          <div className="grid grid-cols-3 gap-2 mt-1" data-testid="pa-catalog-type">
            {[["restaurant", "Restaurant"], ["pharmacie", "Pharmacie"], ["kiosk", "Kiosk"]].map(([val, lbl]) => {
              const active = form.catalog_type === val;
              return (
                <button key={val} type="button" data-testid={`pa-catalog-${val}`} onClick={() => set("catalog_type", val)}
                  className="rounded-xl h-11 text-sm font-bold"
                  style={active ? { background: GREEN, color: "#fff" } : { ...field, color: GREEN }}>
                  {lbl}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] mt-1" style={{ color: "#94a3b8" }}>Un partenaire gère un seul catalogue. Les boissons restent pilotées par La BASE.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold" style={{ color: "#64748b" }}>Base</label>
            <select data-testid="pa-base" value={form.base} onChange={(e) => set("base", e.target.value)}
              className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1" style={field}>
              <option value="payerne">Payerne</option>
              <option value="estavayer">Estavayer-le-Lac</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold" style={{ color: "#64748b" }}>Abonnement souhaité</label>
            <select data-testid="pa-tier" value={form.desired_tier} onChange={(e) => set("desired_tier", e.target.value)}
              className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1" style={field}>
              <option value="premium">Premium (livraison VORTEX + cuisine)</option>
              <option value="gold">Gold (à emporter / livraison autonome)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold" style={{ color: "#64748b" }}>Message (optionnel)</label>
          <textarea data-testid="pa-message" value={form.message} onChange={(e) => set("message", e.target.value)}
            rows={3} className="w-full rounded-xl px-3 py-2 text-sm outline-none mt-1" style={field} />
        </div>

        <button data-testid="pa-submit" onClick={submit} disabled={busy}
          className="mt-2 rounded-xl font-bold text-white h-12" style={{ background: ORANGE, opacity: busy ? 0.6 : 1 }}>
          {busy ? "Envoi…" : "Envoyer ma demande"}
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-xs font-bold" style={{ color: "#64748b" }}>{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl px-3 h-11 text-sm outline-none mt-1" style={field} />
    </div>
  );
}
